import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';
import type { Locale } from '@et/schema';
import { formatSpan } from '@et/schema/date';
import { EvidenceGlyph, Skeleton, StrengthTag, witnessFamily } from '@et/ui';
import { fetchEvidenceDetail, queryKeys, type ContentRev } from '../../lib/api.js';
import { localePath } from '../../lib/locale.js';

/**
 * O cartão que abre ao escolher um ponto.
 *
 * Antes, escolher um pin só acendia a linha correspondente na lista abaixo —
 * correcto e mudo. A informação que faz falta no momento em que alguém aponta
 * para uma marca no tempo é a mesma de sempre: o que é aquilo, de quando,
 * onde está hoje, e **o que não prova**. Essa última linha vai no cartão e não
 * na página de detalhe por decisão: é a promessa do produto, e uma promessa
 * que só aparece a dois cliques de distância é uma promessa opcional.
 *
 * A fotografia entra quando existe uma com licença que permita mostrá-la. Onde
 * não existe, a moldura diz onde o objecto está — que é a resposta honesta e
 * já era a da página de detalhe (I8).
 */
export function EvidencePeek({
  id, locale, rev, onClose,
}: {
  readonly id: string;
  readonly locale: Locale;
  readonly rev: ContentRev;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  const detail = useQuery({
    queryKey: queryKeys.evidenceDetail(rev, locale, id),
    queryFn: () => fetchEvidenceDetail(rev.version, locale, id),
  });

  // Escape fecha. Sem isto o cartao fica no caminho de quem navega por teclado.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const d = detail.data;
  const image = d?.media.find((m) => m.kind === 'image' && m.sourceUrl !== null);
  const family = witnessFamily(d?.evidenceType);
  const supports = (d?.relations ?? []).filter((r) => r.kind === 'supports').slice(0, 4);
  /*
    O que a peca contraria tem o mesmo direito ao cartao.
    
    Filtrar so `supports` fazia o Codice de Beza — a maior excepcao a
    estabilidade do texto que o acervo tem — abrir um cartao que nao dizia
    contra o que e que ele pesa. Num produto que existe para nao declarar
    vencedores, mostrar so o apoio e a falha mais fácil de cometer.
  */
  const challenges = (d?.relations ?? []).filter((r) => r.kind === 'challenges').slice(0, 3);

  return (
    <div
      ref={ref}
      className="et-peek"
      data-family={family}
      role="dialog"
      aria-modal="false"
      aria-label={d?.title ?? t('timeline.label')}
    >
      <button type="button" className="et-peek__close" onClick={onClose} aria-label={t('action.close')}>
        &times;
      </button>

      {detail.isPending && (
        <div className="et-peek__body">
          <Skeleton width="70%" height="22px" />
          <Skeleton width="45%" height="14px" />
        </div>
      )}

      {d !== undefined && (
        <>
          {/*
            A fotografia passa para o lado, e nao para cima.

            Empilhada, ocupava duzentos pixeis de altura numa coluna que os nao
            tinha para dar, e o cartao abria com barra de deslocamento — a ler
            uma peca, a primeira coisa que se fazia era rolar. Ao lado, a mesma
            informacao cabe, e a largura que o grafico emprestou serve para
            alguma coisa.
          */}
          <div className="et-peek__top">
          <div className="et-peek__plate">
            {image !== undefined ? (
              <img src={image.sourceUrl!} alt={image.altText} loading="lazy" />
            ) : (
              <div className="et-peek__noimage">
                <EvidenceGlyph type={d.evidenceType as never} size={26} decorative />
                <span>{d.currentLocation?.institution ?? t('media.noLicensedImage')}</span>
              </div>
            )}
            {image !== undefined && (
              <p className="et-peek__credit">{image.credit}</p>
            )}
          </div>

          <div className="et-peek__head">
            <div className="et-peek__kind">
              <EvidenceGlyph type={d.evidenceType as never} size={14} decorative />
              <span>{t(`lexicon:evidenceType.${d.evidenceType}`)}</span>
              <StrengthTag strength={d.strength as never} />
            </div>

            <h3 className="et-peek__title">{d.title}</h3>

            <dl className="et-peek__facts">
              <div>
                <dt>{t('evidence.event')}</dt>
                <dd>{formatSpan({ earliest: d.event.earliest, latest: d.event.latest }, locale)}</dd>
              </div>
              {d.discovery !== null && (
                <div>
                  <dt>{t('evidence.found')}</dt>
                  <dd>{formatSpan({ earliest: d.discovery.earliest, latest: d.discovery.latest }, locale)}</dd>
                </div>
              )}
              {d.currentLocation !== null && (
                <div>
                  <dt>{t('evidence.heldAt')}</dt>
                  <dd>{d.currentLocation.institution}</dd>
                </div>
              )}
            </dl>
          </div>
          </div>

          <div className="et-peek__body">
            {d.whatWeKnow !== null && <p className="et-peek__prose">{d.whatWeKnow.text}</p>}

            {/*
              O que apoia, e o que nao prova, um ao lado do outro.
              
              Mostrar so o limite fazia o cartao parecer um aviso; mostrar so o
              apoio fazia dele uma vitrine. Sao as duas metades da mesma
              afirmacao, e o produto so e honesto quando aparecem juntas — com
              a significancia da aresta a dizer quanto peso e que aquilo tem.
            */}
            {supports.length > 0 && (
              <div className="et-peek__claims">
                <span className="et-peek__limitLabel">{t('evidence.whatItSupports')}</span>
                <ul>
                  {supports.map((r) => (
                    <li key={`${r.toId}-${r.kind}`}>
                      <span className="et-peek__claimTitle">{r.title ?? r.toId}</span>
                      {r.significance !== null && (
                        <span className="et-peek__sig" data-sig={r.significance}>
                          {t(`lexicon:significance.${r.significance}`)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {challenges.length > 0 && (
              <div className="et-peek__claims" data-kind="challenges">
                <span className="et-peek__limitLabel">{t('evidence.whatItChallenges')}</span>
                <ul>
                  {challenges.map((r) => (
                    <li key={`${r.toId}-${r.kind}`}>
                      <span className="et-peek__claimTitle">{r.title ?? r.toId}</span>
                      {r.significance !== null && (
                        <span className="et-peek__sig" data-sig={r.significance}>
                          {t(`lexicon:significance.${r.significance}`)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/*
              O peek mostrava so o limite. Lido de relance, isso e um veredicto
              contra a peca: quem passa o rato por cima fica a saber o que ela
              nao serve e nada do que serve. O alcance vem primeiro, e o limite
              logo a seguir — as duas metades ou nenhuma.
            */}
            {d.whatItDoesProve[0] !== undefined && (
              <div className="et-peek__scope">
                <span className="et-peek__scopeLabel">{t('evidence.whatItDoesProve')}</span>
                <p>{d.whatItDoesProve[0].text}</p>
              </div>
            )}

            {d.whatItDoesNotProve[0] !== undefined && (
              <div className="et-peek__limit">
                <span className="et-peek__limitLabel">{t('evidence.whatItDoesNotProve')}</span>
                <p>{d.whatItDoesNotProve[0].text}</p>
              </div>
            )}

            {/*
              Caminho absoluto e nao relativo.

              `../evidence/x` a partir do indice de `/:locale` sobe um nivel a
              mais e produz `/evidence/x` — sem locale, e o router responde 404.
              O resto da app usa `localePath` por esta razao; este link era a
              excepcao, e a excepcao estava partida.
            */}
            <Link className="et-peek__more" to={localePath(locale, 'evidence', d.id)}>
              {t('action.openFull')}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
